/**
 * Signed-in user dropdown: frontier models, incident reporting, and account links.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { FRONTIER_MODELS } from '../data/frontierModels';
import BugReportDialog from './BugReportDialog';
import { useDepartmentAlertBadge } from '../hooks/useDepartmentAlertBadge';

/**
 * Picks a friendly greeting name from the signed-in user record.
 * @param {object|null} user
 * @returns {string}
 */
function displayName(user) {
  if (!user) return 'there';
  if (user.firstName) return user.firstName;
  if (user.email) return user.email.split('@')[0];
  return 'there';
}

/**
 * Header dropdown for authenticated Guardian users.
 *
 * @param {object} props
 * @param {object|null} props.user - Guardian user from login/OAuth.
 * @param {string|null} [props.authToken] - Guardian bearer token; the bug-report item needs it.
 * @param {() => void} props.onSignOut
 * @param {(itemId: string) => void} props.onSelectItem - Routes menu clicks to App via useGuardianAuth.
 */
export default function UserMenu({ user, authToken = null, onSignOut, onSelectItem }) {
  const [anchorEl, setAnchorEl] = useState(null);
  // Bug report dialog (2026-09-30). `bugKey` remounts it so every open starts a fresh form.
  const [bugOpen, setBugOpen] = useState(false);
  const [bugKey, setBugKey] = useState(0);
  const open = Boolean(anchorEl);
  const name = displayName(user);
  // Unread team-alert badge on the Department entry (WS-F). Fail-closed to false.
  const hasNewAlerts = useDepartmentAlertBadge(authToken, user);

  /** Opens the anchored menu below the "Hi {name}" button. */
  const handleOpen = (e) => setAnchorEl(e.currentTarget);

  /** Closes the menu without firing navigation. */
  const handleClose = () => setAnchorEl(null);

  /**
   * Closes the menu then delegates navigation to the parent handler.
   * @param {string} itemId
   */
  const handleItem = (itemId) => {
    handleClose();
    onSelectItem?.(itemId);
  };

  /** Closes the menu and opens the bug-report dialog. */
  const handleReportBug = () => {
    handleClose();
    setBugKey((k) => k + 1);
    setBugOpen(true);
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          textTransform: 'none',
          color: 'text.primary',
          fontWeight: 500,
          px: 1.5,
          py: 0.75,
          borderRadius: '20px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'divider',
          },
        }}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        Hi {name}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 240,
              borderRadius: 2,
              boxShadow: 3,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Hi {name}
          </Typography>
          {user?.email && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {user.email}
            </Typography>
          )}
        </Box>

        <Divider />

        <ListSubheader
          sx={{
            lineHeight: '32px',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
            bgcolor: 'transparent',
          }}
        >
          Access our Frontier Models
        </ListSubheader>
        {FRONTIER_MODELS.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => handleItem(item.id)}
            sx={{ pl: 3, py: 1, fontSize: '0.875rem' }}
          >
            {item.label}
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={() => handleItem('incident-reporting')} sx={{ py: 1 }}>
          Incident Reporting
        </MenuItem>
        {/* Small, non-focal bug report entry (2026-09-30); signed-in users only. */}
        {authToken && (
          <MenuItem onClick={handleReportBug} sx={{ py: 1 }}>
            Report a bug
          </MenuItem>
        )}
        {/* My reports (plan Track 5 B6, 2026-09-30). */}
        <MenuItem onClick={() => handleItem('my-reports')} sx={{ py: 1 }}>
          My Reports
        </MenuItem>
        {user?.department ? (
          // Department members (docs/guardian/03 §1.5, 2026-10-21).
          <MenuItem onClick={() => handleItem('department')} sx={{ py: 1 }}>
            <Typography variant="body2">Department</Typography>
            {hasNewAlerts && (
              <Box
                component="span"
                aria-label="New alerts"
                data-testid="dept-alert-badge"
                sx={{ ml: 1, width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', display: 'inline-block' }}
              />
            )}
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => handleItem('profile')} sx={{ py: 1 }}>
          Profile
        </MenuItem>
        <MenuItem onClick={() => handleItem('subscriptions')} sx={{ py: 1 }}>
          Subscriptions
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => {
            handleClose();
            onSignOut?.();
          }}
          sx={{ py: 1, color: 'text.secondary' }}
        >
          Sign out
        </MenuItem>
      </Menu>

      {authToken && (
        <BugReportDialog
          key={bugKey}
          open={bugOpen}
          onClose={() => setBugOpen(false)}
          authToken={authToken}
          user={user}
        />
      )}
    </>
  );
}
